import {
    ChangeDetectionStrategy,
    Component,
    computed,
    contentChildren,
    effect,
    ElementRef,
    inject,
    input,
    InputSignal,
    output,
    OutputEmitterRef,
    Signal,
    viewChild
} from "@angular/core";
import {PageFooterControlDirective} from "../page-footer-control.directive";
import {NavigationService} from "../../navigation/navigation.service";
import {Page, PageAlignment, PageGeometry, PageLayout} from "../../navigation/page";
import {ContentMessageComponent} from "../../notification/message/content-message.component";
import {FooterMessagesComponent} from "../../notification/footer-messages/footer-messages.component";
import {IconButtonComponent} from "../../buttons/icon-button/icon-button.component";
import {IconButtonType} from "../../buttons/icon-button/icon-button";
import {LinkComponent} from "../../buttons/link/link.component";
import {NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {HtmlPipe} from '../../utils/html.pipe';

@Component({
    selector: "woa-modal-page-layout",
    templateUrl: "./modal-page-layout.component.html",
    styleUrls: ["./modal-page-layout.component.scss"],
    providers: [{provide: PageLayout, useExisting: ModalPageLayoutComponent}],
    imports: [
        FooterMessagesComponent,
        HtmlPipe,
        IconButtonComponent,
        LinkComponent,
        NgClass,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalPageLayoutComponent extends PageLayout {
    public page: Page = inject(Page);
    private navigationService: NavigationService = inject(NavigationService);

    PageAlignment = PageAlignment;
    IconButtonType = IconButtonType;

    // header options
    public readonly showHeader: InputSignal<boolean> = input<boolean>(true);
    public readonly showCloseButton: InputSignal<boolean> = input<boolean>(true);
    public readonly showCancelLink: InputSignal<boolean> = input<boolean>(false);
    public readonly closeButtonAlignment: InputSignal<PageAlignment> = input<PageAlignment>(PageAlignment.RIGHT);
    public readonly showRefreshButton: InputSignal<boolean> = input<boolean>(false);
    public readonly showSubtitle: InputSignal<boolean> = input<boolean>(true);
    public readonly title: InputSignal<string> = input<string>();
    public readonly subtitle: InputSignal<string> = input<string>();
    public readonly translationParams: InputSignal<Record<string, string | number>> = input<Record<string, string | number>>({});
    public readonly backgroundColorPair: InputSignal<string> = input<string>("standard-page");
    public readonly contentText: InputSignal<string> = input<string>();
    public readonly showContent: InputSignal<boolean> = input<boolean>(true);
    public readonly padWhenNoContent: InputSignal<boolean> = input<boolean>(true);
    public readonly showFooter: InputSignal<boolean> = input<boolean>(true);
    public readonly showFooterBorder: InputSignal<boolean> = input<boolean>(undefined);
    public readonly footerControlOrientation: InputSignal<"horizontal" | "vertical"> = input<"horizontal" | "vertical">("horizontal");
    public readonly allowSafeArea: InputSignal<boolean> = input<boolean>(true);
    public readonly customGeometry: InputSignal<PageGeometry | undefined> = input<PageGeometry | undefined>();

    public onRefresh: OutputEmitterRef<void> = output<void>();

    public override pageScrollerER: Signal<ElementRef | undefined> = viewChild("scroller");

    public readonly contentMessages: Signal<ReadonlyArray<ContentMessageComponent>> = contentChildren(ContentMessageComponent);
    public readonly footerControls: Signal<ReadonlyArray<PageFooterControlDirective>> = contentChildren(PageFooterControlDirective);

    public readonly showLeftCloseButton: Signal<boolean> = computed(() => this.showCloseButton() && this.closeButtonAlignment() === PageAlignment.LEFT);
    public readonly showRightCloseButton: Signal<boolean> = computed(() => this.showCloseButton() && this.closeButtonAlignment() === PageAlignment.RIGHT);
    public readonly showLeftRefreshButton: Signal<boolean> = computed(() => this.showRefreshButton() && (this.showRightCloseButton() || this.showCancelLink()));
    public readonly showRightRefreshButton: Signal<boolean> = computed(() => this.showRefreshButton() && !(this.showRightCloseButton() || this.showCancelLink()));
    public readonly backgroundColorPairClass: Signal<string> = computed(() => "theme-" + this.backgroundColorPair() + "-color-pair");

    constructor() {
        super();
        effect(() => {
            if (this.customGeometry()) {
                this.page.containerComponentRef?.instance.geometry.set(this.customGeometry()!);
            }
        });
    }

    async close(): Promise<void> {
        if (this.page) {
            void this.navigationService.dismissPage(this.page);
        }
    }
}
